import { useMemo } from "react";

import { type CommentWithOptionalTags } from "./reviewCommentTags";
import { type useAskV2ReviewCommentFilter } from "./useAskV2ReviewCommentFilter";
import { useLoadedReviewCommentGroups } from "./useLoadedReviewCommentGroups";

import { askV2ReviewPanelComments } from "../../utils/visibleReviewComments";

type UseAskV2ReviewPanelCommentsArgs = {
  comments: readonly CommentWithOptionalTags[] | undefined;
  reviewId: string | undefined;
  isCommentFilterVisible: ReturnType<
    typeof useAskV2ReviewCommentFilter
  >["isVisible"];
};

/**
 * The findings the review's left column lists and its artwork pins: filtered,
 * grouped by the section each sat in when the review loaded, and numbered.
 */
export function useAskV2ReviewPanelComments({
  comments,
  reviewId,
  isCommentFilterVisible,
}: UseAskV2ReviewPanelCommentsArgs) {
  const tagFilteredComments = useMemo(() => {
    if (!comments) return [];
    return comments.filter(isCommentFilterVisible);
  }, [isCommentFilterVisible, comments]);

  // The section each finding sat in when this review loaded. Only the order
  // and the numbers below follow it, so a judgment made here renumbers no card
  // or pin; the list files the card under its new section at once.
  const loadedGroups = useLoadedReviewCommentGroups(comments, reviewId);

  // why: a finding rejected in this review stays here, grayed, in its place
  // in this order, and so stays on the artwork as a grayed pin -- the list, the pins and the
  // tab badge all read this one array.
  const panelComments = useMemo(
    () => askV2ReviewPanelComments(tagFilteredComments, loadedGroups),
    [loadedGroups, tagFilteredComments],
  );

  // why: numbered once over every finding the panel holds, in the order of the
  // section each sat in when the review loaded, and handed to the list and the
  // pins alike, so a card and its pin carry one number.
  const commentNumbers = useMemo(() => {
    const numbers: Record<string, number> = {};
    let index = 0;
    // why-not: no isDeleted skip here -- askV2ReviewPanelComments already
    // drops deleted findings, so every card it returns gets a number.
    for (const comment of panelComments) {
      index += 1;
      numbers[comment._id] = index;
    }
    return numbers;
  }, [panelComments]);

  return { loadedGroups, panelComments, commentNumbers };
}
